import './App.css';
import Navbar from './component/navbar/navbar.jsx';
import Main from './component/main.jsx/main.jsx';
import React, { useState, useEffect, useRef } from 'react';
import Profile from './component/createtrip/profile.jsx';
import CreateTrip from './component/createtrip/createtrip.jsx';
import Discover from './component/createtrip/discover.jsx';
import Login from './component/createtrip/login.jsx';
import Signup from './component/createtrip/signup.jsx';
import ViewDetails from './component/details/viewdetail.jsx';
import JoinTrip from './component/details/jointrip.jsx';
import Chatapp from './component/chatapp/groupchat.jsx';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import UserProfile from "./component/chatapp/UserProfile.jsx";

const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;
const API_URL = process.env.REACT_APP_API_URL;

// Render free-tier cold starts can take 30-50s. Give the wake-up call
// enough rope, and retry a couple of times before giving up.
const FETCH_TIMEOUT_MS = 20000;
const MAX_RETRIES = 2;
const RETRY_DELAY_MS = 4000;

// ---------- shared helpers ----------

function getStoredAuth() {
  try {
    const token = localStorage.getItem("token");
    const user = JSON.parse(localStorage.getItem("user") || "null");
    return { token, user };
  } catch {
    return { token: null, user: null };
  }
}

function clearStoredAuth() {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

async function fetchWithTimeout(url, options, timeoutMs) {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(url, { ...options, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

function Spinner({ label }) {
  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 12,
      alignItems: "center", justifyContent: "center",
      minHeight: "60vh",
    }}>
      <div style={{
        width: 34, height: 34,
        border: "3px solid #ffd4c2",
        borderTopColor: "#ff6b35",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
      }} />
      {label && <p style={{ color: "#888", fontSize: 14 }}>{label}</p>}
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

// ---------- route guard ----------
//
// Verifies the token against the backend before rendering a protected page.
//
// Key distinction this makes, unlike a naive try/catch:
//   - Server explicitly says the token is bad (401/403)  -> log out, go to "/"
//   - Server is unreachable / times out / 502-503 (likely a cold-starting
//     free-tier backend) -> DO NOT log out. Retry a few times with backoff,
//     and only fall back to an error state (still logged in) if every
//     retry fails.
function RequireAuth({ element, adminOnly = false }) {
  const [status, setStatus] = useState("checking");
  // "checking" | "waking" | "valid" | "invalid" | "forbidden" | "unreachable"
  const location = useLocation();
  const attemptRef = useRef(0);

  useEffect(() => {
    let cancelled = false;
    const { token, user } = getStoredAuth();

    if (!token || !user?.email) {
      setStatus("invalid");
      return;
    }

    async function verify() {
      for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
        if (cancelled) return;
        attemptRef.current = attempt;
        if (attempt > 0) setStatus("waking"); // backend is likely cold-starting

        try {
          const res = await fetchWithTimeout(
            `${API_URL}/users/${encodeURIComponent(user.email)}`,
            { headers: { Authorization: `Bearer ${token}` } },
            FETCH_TIMEOUT_MS
          );

          if (cancelled) return;

          if (res.status === 401 || res.status === 403) {
            // Server explicitly rejected the token — this is a real logout.
            clearStoredAuth();
            setStatus("invalid");
            return;
          }

          if (!res.ok) {
            // 5xx etc — likely still spinning up. Retry.
            if (attempt < MAX_RETRIES) {
              await sleep(RETRY_DELAY_MS);
              continue;
            }
            setStatus("unreachable");
            return;
          }

          if (adminOnly && user.email !== ADMIN_EMAIL) {
            setStatus("forbidden");
            return;
          }

          setStatus("valid");
          return;
        } catch {
          // Network error / timeout / aborted — backend probably asleep.
          // Do NOT clear the token. Retry, then fall back to "unreachable"
          // (still logged in locally, just couldn't confirm right now).
          if (attempt < MAX_RETRIES) {
            await sleep(RETRY_DELAY_MS);
            continue;
          }
          if (!cancelled) setStatus("unreachable");
          return;
        }
      }
    }

    verify();
    return () => { cancelled = true; };
  }, [adminOnly]);

  if (status === "checking") return <Spinner />;
  if (status === "waking") return <Spinner label="Waking up the server, hang tight…" />;
  if (status === "valid") return element;
  if (status === "forbidden") return <Navigate to="/home" replace />;

  if (status === "unreachable") {
    // Genuinely couldn't reach the server after retries. Don't log the
    // user out for this — let them retry manually instead of silently
    // losing their session.
    return (
      <div style={{ textAlign: "center", padding: "4rem 1rem" }}>
        <h2>Can't reach the server</h2>
        <p style={{ color: "#888" }}>
          This can happen if the server is starting up. Your login is still
          saved — try again in a moment.
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            marginTop: 16, padding: "8px 20px", borderRadius: 8,
            border: "none", background: "#ff6b35", color: "#fff",
            cursor: "pointer",
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  // status === "invalid" -> genuinely not logged in, go to login and
  // remember where they were headed.
  return <Navigate to="/" replace state={{ from: location.pathname }} />;
}

function NotFound() {
  return (
    <div style={{ textAlign: "center", padding: "4rem 1rem" }}>
      <h1>404</h1>
      <p>That page doesn't exist.</p>
    </div>
  );
}

// ---------- app shell ----------

function AppContent() {
  const location = useLocation();
  const hideNavbarRoutes = ['/', '/signup'];
  return (
    <>
      {!hideNavbarRoutes.includes(location.pathname) && <Navbar />}
      <Routes>
        <Route path="/"                element={<Login />} />
        <Route path="/signup"          element={<Signup />} />
        <Route path="/home"            element={<Main />} />
        <Route path="/viewdetails/:id" element={<ViewDetails />} />
        <Route path="/discover"        element={<Discover />} />

        <Route path="/profile/:id"     element={<RequireAuth element={<Profile />} />} />
        <Route path="/join-trip/:id"   element={<RequireAuth element={<JoinTrip />} />} />
        <Route path="/chatapp/:tripId" element={<RequireAuth element={<Chatapp />} />} />
        <Route path="/user/:email"     element={<RequireAuth element={<UserProfile />} />} />

        <Route path="/create-trip" element={<RequireAuth element={<CreateTrip />} adminOnly />} />

        <Route path="*" element={<NotFound />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;